import DOMPurify from 'dompurify';

// Configure DOMPurify with safe defaults for article/page content
const ALLOWED_TAGS = [
  // Structure
  'p', 'div', 'span', 'br', 'hr',
  // Headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // Lists
  'ul', 'ol', 'li',
  // Text formatting
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins', 'sub', 'sup', 'mark',
  // Links and media
  'a', 'img', 'figure', 'figcaption', 'video', 'source', 'audio',
  // Quotes and code
  'blockquote', 'pre', 'code',
  // Tables
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  // Interactive elements (for styled link buttons)
  'button',
  // Other
  'details', 'summary', 'abbr', 'address', 'cite', 'time', 'dfn', 'kbd', 'samp', 'var',
];

const ALLOWED_ATTR = [
  // Common
  'class', 'id', 'style', 'title',
  // Links
  'href', 'target', 'rel',
  // Media
  'src', 'alt', 'width', 'height', 'loading', 'srcset', 'sizes',
  // Video/audio
  'controls', 'autoplay', 'muted', 'loop', 'poster', 'preload', 'playsinline',
  // Tables
  'colspan', 'rowspan', 'scope', 'headers',
  // Time
  'datetime',
  // Data attributes (limited)
  'data-*',
  'data-amazon-asin',
];

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting.
 * Uses DOMPurify with a configured allowlist of safe tags and attributes.
 */
/**
 * Removes WordPress Jetpack related posts blocks and similar navigation blocks from content.
 */
function removeWordPressNavigationBlocks(html: string): string {
  // Remove Jetpack related posts blocks (nested content)
  html = html.replace(/<div[^>]*class="[^"]*wp-block-jetpack-related-posts[^"]*"[^>]*>[\s\S]*?<\/div>\s*<\/div>/gi, '');
  html = html.replace(/<div[^>]*class="[^"]*wp-block-jetpack-related-posts[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
  // Remove WordPress tag cloud blocks
  html = html.replace(/<div[^>]*class="[^"]*wp-block-tag-cloud[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
  // Remove any "Discover more" sections (p, span, h2, h3, or div with text)
  html = html.replace(/<(p|span|div|h2|h3)[^>]*>\s*Discover more[^<]*<\/\1>/gi, '');
  html = html.replace(/<h2[^>]*>Discover more[^<]*<\/h2>/gi, '');
  // Remove Jetpack follow buttons and related sections
  html = html.replace(/<div[^>]*class="[^"]*sharedaddy[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
  return html;
}

const ALLOWED_TAG_SET = new Set(ALLOWED_TAGS);

/**
 * Server-side fallback: DOMPurify needs a real DOM, which the SSR runtime
 * does not have. Strips dangerous tags/attributes with an allowlist so
 * article HTML can be server-rendered for crawlers. The browser re-runs the
 * full DOMPurify pass on hydration.
 */
function serverSanitize(html: string): string {
  return html
    .replace(/<\s*(script|style|iframe|object|embed|form|input|textarea|select)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|style|iframe|object|embed|form|input|textarea|select)\b[^>]*\/?>/gi, '')
    .replace(/\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\s(?:href|src)\s*=\s*(?:"\s*javascript:[^"]*"|'\s*javascript:[^']*'|javascript:[^\s>]*)/gi, '')
    .replace(/<\s*\/?\s*([a-zA-Z][a-zA-Z0-9-]*)\b[^>]*>/g, (match, tag: string) =>
      ALLOWED_TAG_SET.has(tag.toLowerCase()) ? match : '',
    );
}

export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';
  
  // First remove WordPress navigation blocks
  const cleaned = removeWordPressNavigationBlocks(dirty);

  if (typeof window === 'undefined' || typeof DOMPurify.sanitize !== 'function') {
    return serverSanitize(cleaned);
  }

  return DOMPurify.sanitize(cleaned, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true, // Needed for shortcodes like data-amazon-asin
    ADD_ATTR: ['target', 'rel'], // Ensure these can be added
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onmousedown', 'onmouseup', 'onkeydown', 'onkeyup', 'onkeypress', 'onfocus', 'onblur', 'onsubmit', 'onchange', 'ondragstart', 'ondrag', 'ondrop'],
    // Force all links to have safe rel attribute
    FORCE_BODY: true,
  });
}


/**
 * Hook to configure DOMPurify with additional safety measures.
 * Call once on app initialization if needed.
 */
export function configureSanitizer(): void {
  // Add hook to force safe link attributes
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // Force target="_blank" links to have noopener noreferrer
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
    // Force all external links to have noopener noreferrer
    if (node.tagName === 'A') {
      const href = node.getAttribute('href');
      if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
        const existingRel = node.getAttribute('rel') || '';
        if (!existingRel.includes('noopener')) {
          node.setAttribute('rel', `${existingRel} noopener noreferrer`.trim());
        }
      }
    }
  });
}
